package br.com.vvrefeicoes.portal.core.util.spreadsheet

import kotlinx.browser.document
import kotlinx.coroutines.suspendCancellableCoroutine
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.events.Event
import org.w3c.files.FileReader
import kotlin.coroutines.resume

actual fun isSpreadsheetImportSupported(): Boolean = true

actual suspend fun pickSpreadsheetGrid(): List<List<String>>? =
    suspendCancellableCoroutine { continuation ->
        val input = document.createElement("input") as HTMLInputElement
        input.type = "file"
        input.accept = ".csv,.xlsx,.xls"
        // Fechar o seletor sem escolher arquivo não dispara "change": sem tratar o cancelamento a
        // importação ficaria pendente para sempre, com o botão de importar desabilitado.
        val onCancel: (Event) -> Unit = { if (continuation.isActive) continuation.resume(null) }
        input.addEventListener("cancel", onCancel)
        input.onchange = {
            val file = input.files?.item(0)
            if (file == null) {
                continuation.resume(null)
            } else {
                val reader = FileReader()
                reader.onload = {
                    // No alvo Wasm o resultado é uma referência JS: `as? String` devolve null e a
                    // importação era abandonada em silêncio. `toString()` funciona em JS e em Wasm.
                    val binary = reader.result?.toString()
                    if (binary == null) {
                        continuation.resume(null)
                    } else {
                        val bytes = ByteArray(binary.length) { index -> binary[index].code.toByte() }
                        runCatching { parseSpreadsheet(bytes) }
                            .fold(
                                onSuccess = { continuation.resume(it) },
                                onFailure = { continuation.resumeWith(Result.failure(it)) },
                            )
                    }
                }
                reader.onerror = { continuation.resume(null) }
                reader.readAsBinaryString(file)
            }
        }
        input.click()
    }

actual suspend fun pickPurchasesSpreadsheet(sheetNameContains: String?): PickedSpreadsheet? =
    suspendCancellableCoroutine { continuation ->
        val input = document.createElement("input") as HTMLInputElement
        input.type = "file"
        input.accept = ".xlsx"
        val onCancel: (Event) -> Unit = { if (continuation.isActive) continuation.resume(null) }
        input.addEventListener("cancel", onCancel)
        input.onchange = {
            val file = input.files?.item(0)
            if (file == null) {
                continuation.resume(null)
            } else {
                val reader = FileReader()
                reader.onload = {
                    val binary = reader.result?.toString()
                    if (binary == null) {
                        continuation.resume(null)
                    } else {
                        val bytes = ByteArray(binary.length) { index -> binary[index].code.toByte() }
                        runCatching {
                            XlsxReader.read(bytes) { name ->
                                sheetNameContains == null || name.contains(sheetNameContains, ignoreCase = true)
                            }
                        }.fold(
                            onSuccess = { rows -> continuation.resume(PickedSpreadsheet(file.name, rows)) },
                            onFailure = { continuation.resumeWith(Result.failure(it)) },
                        )
                    }
                }
                reader.onerror = { continuation.resume(null) }
                reader.readAsBinaryString(file)
            }
        }
        input.click()
    }

private fun parseSpreadsheet(bytes: ByteArray): List<List<String>> =
    when {
        bytes.size >= 2 && bytes[0] == 0x50.toByte() && bytes[1] == 0x4B.toByte() -> XlsxReader.read(bytes)
        XlsReader.isXls(bytes) -> XlsReader.read(bytes)
        else -> CsvReader.read(bytes.decodeToString())
    }
